import { useState, useEffect, useRef, useCallback } from "react";

// ── useWakeLock ───────────────────────────────────────────────
/**
 * Manages the Screen Wake Lock API so the display stays on
 * while the teleprompter is rolling.
 *
 * Returns { active, supported, toggle, acquire, release }
 */
export function useWakeLock() {
  const [active, setActive]       = useState(false);
  const [supported, setSupported] = useState(true);
  const lockRef   = useRef(null);
  const wantedRef = useRef(false);

  useEffect(() => {
    if (!("wakeLock" in navigator)) setSupported(false);
  }, []);

  const acquire = useCallback(async () => {
    if (!("wakeLock" in navigator)) return;
    wantedRef.current = true;
    try {
      lockRef.current = await navigator.wakeLock.request("screen");
      setActive(true);
      lockRef.current.addEventListener("release", () => {
        setActive(false);
        lockRef.current = null;
      });
    } catch (_) {}
  }, []);

  const release = useCallback(() => {
    wantedRef.current = false;
    if (lockRef.current) { lockRef.current.release(); lockRef.current = null; }
    setActive(false);
  }, []);

  // Re-acquire when tab becomes visible again (browser revokes on hide)
  useEffect(() => {
    const onVisible = async () => {
      if (wantedRef.current && document.visibilityState === "visible" && !lockRef.current) {
        await acquire();
      }
    };
    document.addEventListener("visibilitychange", onVisible);
    return () => document.removeEventListener("visibilitychange", onVisible);
  }, [acquire]);

  const toggle = useCallback(() => {
    active ? release() : acquire();
  }, [active, acquire, release]);

  return { active, supported, toggle, acquire, release };
}

// ── useVoiceScroll ────────────────────────────────────────────
/**
 * Listens to the microphone and fires `onAdvance` each time a
 * pause in speech is detected (speaking → silence for ≥ silenceMs).
 *
 * @param {object} opts
 * @param {boolean}  opts.enabled
 * @param {function} opts.onAdvance        - called on each detected pause
 * @param {number}   [opts.silenceThreshold=0.015] - RMS threshold
 * @param {number}   [opts.silenceMs=600]  - ms of silence before advance
 *
 * Returns { error: string|null, level: number 0–1 }
 */
export function useVoiceScroll({
  enabled,
  onAdvance,
  silenceThreshold = 0.015,
  silenceMs        = 600,
}) {
  const streamRef    = useRef(null);
  const ctxRef       = useRef(null);
  const analyserRef  = useRef(null);
  const rafRef       = useRef(null);
  const silenceStart = useRef(null);
  const wasSpeaking  = useRef(false);

  const [error, setError] = useState(null);
  const [level, setLevel] = useState(0);

  // Resume AudioContext when tab regains focus
  useEffect(() => {
    const onVisible = () => {
      if (
        document.visibilityState === "visible" &&
        ctxRef.current &&
        ctxRef.current.state === "suspended"
      ) {
        ctxRef.current.resume().catch(() => {});
      }
    };
    document.addEventListener("visibilitychange", onVisible);
    return () => document.removeEventListener("visibilitychange", onVisible);
  }, []);

  useEffect(() => {
    if (!enabled) {
      cancelAnimationFrame(rafRef.current);
      streamRef.current?.getTracks().forEach(t => t.stop());
      streamRef.current = null;
      if (ctxRef.current) { ctxRef.current.close(); ctxRef.current = null; }
      setError(null);
      setLevel(0);
      return;
    }

    let alive = true;

    (async () => {
      try {
        const stream = await navigator.mediaDevices.getUserMedia({ audio: true, video: false });
        if (!alive) { stream.getTracks().forEach(t => t.stop()); return; }

        streamRef.current = stream;
        const ctx      = new (window.AudioContext || window.webkitAudioContext)();
        ctxRef.current = ctx;

        const source   = ctx.createMediaStreamSource(stream);
        const analyser = ctx.createAnalyser();
        analyser.fftSize    = 256;
        analyserRef.current = analyser;
        source.connect(analyser);

        const buf = new Float32Array(analyser.fftSize);

        const tick = () => {
          if (!alive) return;
          if (ctx.state === "suspended") ctx.resume().catch(() => {});
          rafRef.current = requestAnimationFrame(tick);

          analyser.getFloatTimeDomainData(buf);
          let sum = 0;
          for (let i = 0; i < buf.length; i++) sum += buf[i] * buf[i];
          const rms = Math.sqrt(sum / buf.length);
          setLevel(Math.min(1, rms / 0.1));

          const speaking = rms > silenceThreshold;
          if (speaking) {
            wasSpeaking.current  = true;
            silenceStart.current = null;
          } else {
            if (wasSpeaking.current) {
              if (silenceStart.current === null) {
                silenceStart.current = Date.now();
              } else if (Date.now() - silenceStart.current >= silenceMs) {
                silenceStart.current = null;
                wasSpeaking.current  = false;
                onAdvance();
              }
            }
          }
        };

        rafRef.current = requestAnimationFrame(tick);
      } catch (e) {
        if (alive) setError(e.message || "Mic access denied");
      }
    })();

    return () => {
      alive = false;
      cancelAnimationFrame(rafRef.current);
      streamRef.current?.getTracks().forEach(t => t.stop());
      streamRef.current = null;
      if (ctxRef.current) { ctxRef.current.close(); ctxRef.current = null; }
    };
  }, [enabled]); // eslint-disable-line react-hooks/exhaustive-deps

  return { error, level };
}

// ── useToast ──────────────────────────────────────────────────
/**
 * Simple transient toast notification.
 * Returns { toastMsg, toastVisible, showToast }
 */
export function useToast(durationMs = 2200) {
  const [toastMsg,     setToastMsg]     = useState("");
  const [toastVisible, setToastVisible] = useState(false);
  const timerRef = useRef(null);

  const showToast = useCallback((msg) => {
    clearTimeout(timerRef.current);
    setToastMsg(msg);
    setToastVisible(true);
    timerRef.current = setTimeout(() => setToastVisible(false), durationMs);
  }, [durationMs]);

  useEffect(() => () => clearTimeout(timerRef.current), []);

  return { toastMsg, toastVisible, showToast };
}